import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  FiCheckSquare,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiList,
  FiRefreshCw,
  FiFilter,
  FiX,
  FiRotateCcw,
  FiCalendar,
} from "react-icons/fi";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../api/axios";
import DynamicTable from "../Common/DynamicTable";
import { taskColumns } from "./TasksColumn";
import LeadDrawer from "../Leads/LeadDrawer";
import DealsDrawer from "../Deals/DealsDrawer";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";
import "../../assets/css/Tasks/MyTasksList.css";

const DUE_DATE_LABELS = {
  today: "Today",
  tomorrow: "Tomorrow",
  this_week: "This Week",
  next_7_days: "Next 7 Days",
  this_month: "This Month",
  overdue: "Overdue",
  no_due_date: "No Due Date",
  custom: "Custom Range",
};

export default function MyTasksList() {
  const [tasks, setTasks] = useState([]);
  const [cards, setCards] = useState({
    total_tasks: 0,
    pending_tasks: 0,
    in_progress_tasks: 0,
    completed_tasks: 0,
    overdue_tasks: 0,
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dueDateFilter, setDueDateFilter] = useState("");
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealDrawerOpen, setDealDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("overview");
  const [sortConfig, setSortConfig] = useState({ key: "due_date", direction: "asc" });
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);
  const location = useLocation();

  const user = useMemo(() => JSON.parse(sessionStorage.getItem("user") || "{}"), []);
  const normalizedRole = (user?.role || "").toLowerCase().replace(/\s+/g, "_");
  const isExecutive = normalizedRole === "sales_executive";

  const fetchData = useCallback(async (filter = dateFilter) => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const taskEndpoint = isExecutive ? "/my-tasks" : "/tasks";
      const cardsEndpoint = isExecutive ? "/tasks/cards?mine=true" : "/tasks/cards";
      const params = buildDateFilterParams(filter);
      const hasParams = Object.keys(params).length > 0;

      const [tasksRes, cardsRes] = await Promise.all([
        (async () => {
          try {
            return await (hasParams ? api.get(taskEndpoint, { params }) : api.get(taskEndpoint));
          } catch (err) {
            console.error("Failed to fetch tasks:", err);
            return { data: [] };
          }
        })(),
        (async () => {
          try {
            return await (hasParams ? api.get(cardsEndpoint, { params }) : api.get(cardsEndpoint));
          } catch (err) {
            console.error("Failed to fetch task cards:", err);
            return {
              data: {
                total_tasks: 0,
                pending_tasks: 0,
                in_progress_tasks: 0,
                completed_tasks: 0,
                overdue_tasks: 0,
              },
            };
          }
        })(),
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      setTasks(Array.isArray(tasksRes?.data) ? tasksRes.data : []);
      setCards(
        cardsRes?.data || {
          total_tasks: 0,
          pending_tasks: 0,
          in_progress_tasks: 0,
          completed_tasks: 0,
          overdue_tasks: 0,
        }
      );
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Failed to fetch tasks data:", err);
      setTasks([]);
      setCards({
        total_tasks: 0,
        pending_tasks: 0,
        in_progress_tasks: 0,
        completed_tasks: 0,
        overdue_tasks: 0,
      });
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [isExecutive, dateFilter]);

  const fetchTasks = useCallback((filter) => fetchData(filter), [fetchData]);
  const fetchCards = useCallback((filter) => fetchData(filter), [fetchData]);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle row selection
  const handleSelect = (id) => {
    const sId = String(id);
    setSelectedIds((prev) =>
      prev.includes(sId) ? prev.filter((x) => x !== sId) : [...prev, sId]
    );
  };

  const handleSelectAll = () => {
    const allIds = sortedTasks.map((t) => String(t.id));
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  // Quick action: Complete Task
  const handleQuickComplete = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}/complete`);
      Swal.fire({
        icon: "success",
        title: "Task Completed",
        text: `"${task.title}" marked as completed.`,
        timer: 1500,
        showConfirmButton: false,
      });
      // Optimistic update
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, status: "Completed", completed_at: new Date().toISOString() }
            : t
        )
      );
      fetchCards();
    } catch (err) {
      console.error("Failed to complete task:", err);
    }
  };

  // Open related deal or lead drawer
  const openTask = async (taskOrId) => {
    let target = taskOrId;
    if (typeof taskOrId === "number" || typeof taskOrId === "string") {
      target = tasks.find((t) => String(t.id) === String(taskOrId));
      if (!target) {
        try {
          const res = await api.get(`/tasks/${taskOrId}`);
          target = res.data;
        } catch (err) {
          console.error("Failed to load task:", err);
        }
      }
    }
    if (!target) return;

    // 1. Prioritize Deal context if task is linked to a deal
    if (target.deal_id) {
      try {
        const res = await api.get(`/deal/${target.deal_id}`);
        setSelectedDeal(res.data);
        setDrawerTab(location.state?.openFollowUp ? "timeline" : "overview");
        setDealDrawerOpen(true);
        return;
      } catch (err) {
        console.error("Failed to load deal details:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to load deal",
          text: "Unable to load the related deal details.",
        });
        return;
      }
    }

    // 2. Fallback to Lead context if task is linked to a lead
    const leadId = target.lead_id || target.lead?.id;
    if (leadId) {
      try {
        const res = await api.get(`/lead/get/${leadId}`);
        setSelectedLead(res.data);
        setDrawerTab(location.state?.openFollowUp ? "activity" : "overview");
        setLeadDrawerOpen(true);
        return;
      } catch (err) {
        console.error("Failed to load lead details:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to load lead",
          text: "Unable to load the related lead details.",
        });
        return;
      }
    }

    Swal.fire({
      icon: "info",
      title: "No Related Lead",
      text: "This task is not linked to any lead.",
    });
  };

  useEffect(() => {
    if (location.state?.openTaskId) {
      setDrawerTab(location.state.openFollowUp ? "activity" : "overview");
      openTask(location.state.openTaskId);
    }
  }, [location.state]);

  // Bulk delete (if authorized)
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;

    const result = await Swal.fire({
      title: "Delete Selected Tasks?",
      text: `Are you sure you want to delete ${selectedIds.length} task(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    try {
      await api.post("/tasks/bulk-delete", {
        task_ids: selectedIds.map((id) => Number(id)),
      });

      setTasks((prev) => prev.filter((t) => !selectedIds.includes(String(t.id))));
      setSelectedIds([]);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Tasks deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
      fetchCards();
    } catch (err) {
      console.error("Failed to delete tasks:", err);
    }
  };

  // Stat card quick filter toggle
  const handleCardFilter = (filterKey) => {
    if (statusFilter === filterKey) {
      setStatusFilter("");
    } else {
      setStatusFilter(filterKey);
    }
  };

  // Client-side filtering
  const filteredTasks = useMemo(() => {
    const safeSearch = search.toLowerCase().trim();
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    return tasks.filter((task) => {
      // Search filter across title, description, and entity relations
      const searchTarget = `
        ${task.title || ""}
        ${task.description || ""}
        ${task.organization_name || ""}
        ${task.lead_title || ""}
        ${task.deal_title || ""}
        ${task.contact_name || ""}
      `.toLowerCase();

      const matchesSearch = !safeSearch || searchTarget.includes(safeSearch);

      // Status filter
      let matchesStatus = true;
      if (statusFilter) {
        if (statusFilter.toLowerCase() === "overdue") {
          matchesStatus =
            task.due_date &&
            task.status !== "Completed" &&
            task.status !== "Cancelled" &&
            new Date(task.due_date) < now;
        } else {
          matchesStatus =
            task.status?.toLowerCase() === statusFilter.toLowerCase();
        }
      }

      // Due Date filter
      let matchesDueDate = true;
      if (dueDateFilter) {
        if (!task.due_date) {
          matchesDueDate = dueDateFilter === "no_due_date";
        } else {
          const taskDueDate = new Date(task.due_date);

          if (dueDateFilter === "no_due_date") {
            matchesDueDate = false;
          } else if (dueDateFilter === "today") {
            matchesDueDate = taskDueDate >= startOfToday && taskDueDate <= endOfToday;
          } else if (dueDateFilter === "tomorrow") {
            const startOfTomorrow = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
            const endOfTomorrow = new Date(endOfToday.getTime() + 24 * 60 * 60 * 1000);
            matchesDueDate = taskDueDate >= startOfTomorrow && taskDueDate <= endOfTomorrow;
          } else if (dueDateFilter === "this_week") {
            const dayOfWeek = now.getDay();
            const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
            const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilSunday, 23, 59, 59, 999);
            matchesDueDate = taskDueDate >= startOfToday && taskDueDate <= endOfWeek;
          } else if (dueDateFilter === "next_7_days") {
            const endOf7Days = new Date(startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000 - 1);
            matchesDueDate = taskDueDate >= startOfToday && taskDueDate <= endOf7Days;
          } else if (dueDateFilter === "this_month") {
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
            matchesDueDate = taskDueDate >= startOfMonth && taskDueDate <= endOfMonth;
          } else if (dueDateFilter === "overdue") {
            matchesDueDate = taskDueDate < now && task.status !== "Completed" && task.status !== "Cancelled";
          } else if (dueDateFilter === "custom") {
            if (customDateRange.from) {
              const fromDate = new Date(customDateRange.from);
              fromDate.setHours(0, 0, 0, 0);
              if (taskDueDate < fromDate) matchesDueDate = false;
            }
            if (customDateRange.to) {
              const toDate = new Date(customDateRange.to);
              toDate.setHours(23, 59, 59, 999);
              if (taskDueDate > toDate) matchesDueDate = false;
            }
          }
        }
      }

      return matchesSearch && matchesStatus && matchesDueDate;
    });
  }, [tasks, search, statusFilter, dueDateFilter, customDateRange]);

  const hasActiveFilters = Boolean(
    search ||
    statusFilter ||
    dueDateFilter ||
    customDateRange.from ||
    customDateRange.to
  );

  const resetAllFilters = () => {
    setSearch("");
    setStatusFilter("");
    setDueDateFilter("");
    setCustomDateRange({ from: "", to: "" });
  };

  // Sorting
  const sortedTasks = useMemo(() => {
    if (!sortConfig.key) return filteredTasks;

    return [...filteredTasks].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "due_date") {
        aVal = aVal ? new Date(aVal).getTime() : Infinity;
        bVal = bVal ? new Date(bVal).getTime() : Infinity;
      }

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredTasks, sortConfig]);

  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const canDelete =
    normalizedRole === "super_admin";

  const pageTitle =
    normalizedRole === "super_admin"
      ? "All Tasks"
      : normalizedRole === "sales_manager"
        ? "Team Tasks"
        : "My Tasks";

  const pageSubtitle =
    normalizedRole === "super_admin"
      ? "Overview and management of all organizational tasks across the CRM."
      : normalizedRole === "sales_manager"
        ? "Monitor, prioritize, and manage tasks across your sales executive team."
        : "Manage, prioritize, and complete your assigned operational tasks.";

  return (
    <div className="tasks-wrapper">
      {/* Page Header */}
      <div className="tasks-header">
        <div className="tasks-header-left">
          <h2>{pageTitle}</h2>
          <p>{pageSubtitle}</p>
        </div>

        <div className="tasks-header-actions">
          <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
          <button
            className="btn-refresh-task"
            onClick={() => fetchTasks(dateFilter)}
            title="Refresh Tasks"
            aria-label="Refresh Tasks"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className={`tasks-stats-grid${loading ? " refreshing" : ""}`}>
        <div
          className={`task-stat-card ${statusFilter === "" ? "active-filter" : ""}`}
          onClick={() => setStatusFilter("")}
          title="Click to view all tasks"
        >
          <div className="task-stat-icon total">
            <FiList />
          </div>
          <div className="task-stat-info">
            <p>Total Tasks</p>
            <h3>{cards?.total_tasks ?? 0}</h3>
          </div>
        </div>

        <div
          className={`task-stat-card ${statusFilter === "Pending" ? "active-filter" : ""}`}
          onClick={() => handleCardFilter("Pending")}
          title="Click to filter by Pending"
        >
          <div className="task-stat-icon pending">
            <FiClock />
          </div>
          <div className="task-stat-info">
            <p>Pending</p>
            <h3>{cards?.pending_tasks ?? 0}</h3>
          </div>
        </div>

        <div
          className={`task-stat-card ${statusFilter === "In Progress" ? "active-filter" : ""}`}
          onClick={() => handleCardFilter("In Progress")}
          title="Click to filter by In Progress"
        >
          <div className="task-stat-icon in-progress">
            <FiCheckSquare />
          </div>
          <div className="task-stat-info">
            <p>In Progress</p>
            <h3>{cards?.in_progress_tasks ?? 0}</h3>
          </div>
        </div>

        <div
          className={`task-stat-card ${statusFilter === "Completed" ? "active-filter" : ""}`}
          onClick={() => handleCardFilter("Completed")}
          title="Click to filter by Completed"
        >
          <div className="task-stat-icon completed">
            <FiCheckCircle />
          </div>
          <div className="task-stat-info">
            <p>Completed</p>
            <h3>{cards?.completed_tasks ?? 0}</h3>
          </div>
        </div>

        <div
          className={`task-stat-card ${statusFilter === "overdue" ? "active-filter" : ""}`}
          onClick={() => handleCardFilter("overdue")}
          title="Click to filter by Overdue"
        >
          <div className="task-stat-icon overdue">
            <FiAlertCircle />
          </div>
          <div className="task-stat-info">
            <p>Overdue</p>
            <h3 style={{ color: (cards?.overdue_tasks ?? 0) > 0 ? "#dc2626" : undefined }}>
              {cards?.overdue_tasks ?? 0}
            </h3>
          </div>
        </div>
      </div>

      {/* Dynamic Table Card */}
      <div className="table-card">
        {/* Custom Due Date Range Input Bar */}
        {dueDateFilter === "custom" && (
          <div className="task-custom-date-row">
            <div className="task-custom-date-header">
              <FiCalendar />
              <span>Custom Due Date Range:</span>
            </div>
            <div className="task-custom-date-inputs">
              <label>
                From
                <input
                  type="date"
                  aria-label="Filter Due Date From"
                  value={customDateRange.from}
                  onChange={(e) =>
                    setCustomDateRange((prev) => ({ ...prev, from: e.target.value }))
                  }
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  aria-label="Filter Due Date To"
                  value={customDateRange.to}
                  onChange={(e) =>
                    setCustomDateRange((prev) => ({ ...prev, to: e.target.value }))
                  }
                />
              </label>
              {(customDateRange.from || customDateRange.to) && (
                <button
                  type="button"
                  className="btn-clear-date-range"
                  onClick={() => setCustomDateRange({ from: "", to: "" })}
                  title="Clear custom date range"
                >
                  Clear Dates
                </button>
              )}
            </div>
          </div>
        )}

        {/* Active Filters Bar */}
        {hasActiveFilters && (
          <div className="task-active-filters-bar">
            <div className="task-active-filters-left">
              <span className="task-active-filters-title">
                <FiFilter /> Active Filters:
              </span>
              <div className="task-active-chips">
                {search && (
                  <span className="task-filter-chip">
                    Search: "{search}"
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      aria-label="Remove search filter"
                    >
                      <FiX />
                    </button>
                  </span>
                )}
                {statusFilter && (
                  <span className="task-filter-chip">
                    Status: {statusFilter}
                    <button
                      type="button"
                      onClick={() => setStatusFilter("")}
                      aria-label="Remove status filter"
                    >
                      <FiX />
                    </button>
                  </span>
                )}
                {dueDateFilter && (
                  <span className="task-filter-chip">
                    Due: {DUE_DATE_LABELS[dueDateFilter] || dueDateFilter}
                    <button
                      type="button"
                      onClick={() => {
                        setDueDateFilter("");
                        setCustomDateRange({ from: "", to: "" });
                      }}
                      aria-label="Remove due date filter"
                    >
                      <FiX />
                    </button>
                  </span>
                )}
                {dueDateFilter === "custom" &&
                  (customDateRange.from || customDateRange.to) && (
                    <span className="task-filter-chip">
                      Range: {customDateRange.from || "Start"} →{" "}
                      {customDateRange.to || "End"}
                      <button
                        type="button"
                        onClick={() => setCustomDateRange({ from: "", to: "" })}
                        aria-label="Remove custom date range"
                      >
                        <FiX />
                      </button>
                    </span>
                  )}
              </div>
            </div>

            <div className="task-active-filters-right">
              <span className="task-results-counter">
                Showing <strong>{sortedTasks.length}</strong> of{" "}
                <strong>{tasks.length}</strong> tasks
              </span>
              <button
                type="button"
                className="btn-reset-filters"
                onClick={resetAllFilters}
                title="Reset all filters"
                aria-label="Reset all filters"
              >
                <FiRotateCcw /> Reset Filters
              </button>
            </div>
          </div>
        )}

        <DynamicTable
          data={sortedTasks}
          columns={taskColumns({
            selectedIds,
            handleSelect,
            handleSelectAll,
            totalRows: sortedTasks.length,
          })}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by task title, description, entity..."
          onRowClick={(taskId) => {
            setDrawerTab("overview");
            openTask(taskId);
          }}
          selectedIds={selectedIds}
          selectedCount={selectedIds.length}
          onBulkDelete={handleBulkDelete}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
          canDelete={canDelete}
          storageKey="my_tasks_visible_columns_v2"
          filters={[
            {
              name: "due_date",
              value: dueDateFilter,
              onChange: setDueDateFilter,
              options: [
                { label: "Due Today", value: "today" },
                { label: "Due Tomorrow", value: "tomorrow" },
                { label: "Due This Week", value: "this_week" },
                { label: "Next 7 Days", value: "next_7_days" },
                { label: "Due This Month", value: "this_month" },
                { label: "Overdue", value: "overdue" },
                { label: "No Due Date", value: "no_due_date" },
                { label: "Custom Range...", value: "custom" },
              ],
              placeholder: "All Due Dates",
            },
          ]}
        />
      </div>

      {/* Related Lead Drawer */}
      <LeadDrawer
        lead={selectedLead}
        isOpen={leadDrawerOpen}
        onClose={() => {
          setLeadDrawerOpen(false);
          setSelectedLead(null);
          setDrawerTab("overview");
        }}
        refreshLeads={fetchTasks}
        initialTab={drawerTab}
      />

      {/* Related Deal Drawer */}
      <DealsDrawer
        dealId={selectedDeal?.id}
        deal={selectedDeal}
        isOpen={dealDrawerOpen}
        onClose={() => {
          setDealDrawerOpen(false);
          setSelectedDeal(null);
          setDrawerTab("overview");
        }}
        refreshDeals={fetchTasks}
        initialTab={drawerTab}
      />
    </div>
  );
}
